import type { ChildResource } from '@repo/api/parent/useChildrenResource';
import type { ErrorMessage } from '@repo/types/messages/error';
import type { UserPublic } from '@repo/types/users';
import type { ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { ChevronDownIcon, TriangleAlertIcon } from 'lucide-react';

import { ChildAvatar, getChildFullName } from '@/components/features/parent/ChildAvatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useChildSectionCollapse } from '@/hooks/parent';

interface ChildSectionListBaseProps<TData> {
  /** Per-child rows from a `useGetChildren*` hook. */
  results: ChildResource<TData>[];
  /** Children whose request failed, from the same hook. */
  failed: UserPublic[];
  isPending: boolean;
  isError?: boolean;
  errorMessage?: ErrorMessage;
  /** Renders one child's data. Only called when `isEmpty` is false. */
  children: (data: TData, child: UserPublic) => ReactNode;
  /**
   * Short line kept in the child's header, e.g. `Wyników: 60`. It is the only
   * thing a collapsed section says about its contents, so prefer a count.
   */
  summary?: (data: TData, child: UserPublic) => ReactNode;
  /** Shown when the parent has no linked children at all. */
  noChildrenMessage?: string;
  /** Whether each child's section folds to its header. */
  collapsible?: boolean;
  /** Which sections start open before the parent has toggled anything. */
  defaultExpanded?: 'first' | 'all' | 'none';
  className?: string;
}

// `isEmpty` and `emptyMessage` come as a pair or not at all: an empty check without copy would render a blank `EmptyState`.
type ChildSectionListEmptyProps<TData> =
  | {
      /** Whether this child's payload has nothing to show. */
      isEmpty: (data: TData) => boolean;
      /** Shown in place of a child's section when `isEmpty` returns true. */
      emptyMessage: string;
    }
  | {
      isEmpty?: never;
      emptyMessage?: never;
    };

type ChildSectionListProps<TData> = ChildSectionListBaseProps<TData> & ChildSectionListEmptyProps<TData>;

/**
 * Renders one section per child for an aggregated parent view.
 *
 * Parent pages show every linked child at once rather than switching between
 * them, and each of them needs the same three states per child — data, nothing
 * yet, or a link that no longer grants access. Keeping that here is what lets
 * each page stay a thin wrapper over its hook.
 *
 * A child whose request failed is listed separately: a revoked link should not
 * hide their siblings' data, but it should not silently look like an empty
 * record either. Those sections stay open — a warning nobody can see is worse
 * than a long page.
 */
export function ChildSectionList<TData>(childSectionListProps: ChildSectionListProps<TData>) {
  const {
    results,
    failed,
    isPending,
    isError = false,
    errorMessage,
    children,
    summary,
    isEmpty,
    emptyMessage,
    noChildrenMessage = 'Brak przypisanych dzieci',
    collapsible = true,
    defaultExpanded = 'first',
    className,
  } = childSectionListProps;

  const { isExpanded, setExpanded } = useChildSectionCollapse(defaultExpanded === 'all' ? 'all' : defaultExpanded === 'none' ? undefined : results[0]?.child.id);

  if (isPending) {
    return (
      <div className={cn('flex flex-col gap-6', className)}>
        <Skeleton className={'h-32 w-full'} />
        <Skeleton className={'h-32 w-full'} />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant={'destructive'}>
        <TriangleAlertIcon />
        <AlertDescription>{errorMessage?.detail ?? 'Nie udało się pobrać listy dzieci.'}</AlertDescription>
      </Alert>
    );
  }

  if (!results.length && !failed.length) {
    return (
      <EmptyState>
        <EmptyStateContent>
          <EmptyStateDescription>{noChildrenMessage}</EmptyStateDescription>
        </EmptyStateContent>
      </EmptyState>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {results.map(({ child, data }) => {
        const body = isEmpty?.(data) ? (
          <EmptyState>
            <EmptyStateContent>
              <EmptyStateDescription>{emptyMessage}</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        ) : (
          children(data, child)
        );

        if (!collapsible) {
          return (
            <section key={child.id} className={'flex flex-col gap-3 pb-8'}>
              <ChildSectionHeader child={child} summary={summary?.(data, child)} />
              {body}
            </section>
          );
        }

        return (
          <Collapsible key={child.id} open={isExpanded(child.id)} onOpenChange={(open) => setExpanded(child.id, open)} asChild>
            <section className={'flex flex-col gap-3'}>
              <ChildSectionHeader child={child} summary={summary?.(data, child)} collapsible />
              <CollapsibleContent className={'flex flex-col gap-3 pb-8'}>{body}</CollapsibleContent>
            </section>
          </Collapsible>
        );
      })}

      {failed.map((child) => (
        <section key={child.id} className={'flex flex-col gap-3'}>
          <ChildSectionHeader child={child} />
          <Alert variant={'warning'}>
            <TriangleAlertIcon />
            <AlertDescription>Nie udało się pobrać danych tego dziecka. Sprawdź, czy konto jest nadal powiązane.</AlertDescription>
          </Alert>
        </section>
      ))}
    </div>
  );
}

interface ChildSectionHeaderProps {
  child: UserPublic;
  summary?: ReactNode;
  /** Renders the row as the section's collapse trigger. */
  collapsible?: boolean;
}

/**
 * The row naming a child, and — when the section folds — the whole hit area
 * that folds it. The heading wraps the button rather than the other way round,
 * so the outline stays a list of children whether they are open or shut.
 */
export function ChildSectionHeader(childSectionHeaderProps: ChildSectionHeaderProps) {
  const { child, summary, collapsible = false } = childSectionHeaderProps;

  const content = (
    <>
      <ChildAvatar child={child} />
      <span>{getChildFullName(child)}</span>
      {summary ? <span className={'text-muted-foreground text-sm font-normal'}>{summary}</span> : null}
      {collapsible ? <ChevronDownIcon className={'text-muted-foreground ms-auto size-4 shrink-0 transition-transform group-data-[state=closed]/child-header:-rotate-90'} aria-hidden={'true'} /> : null}
    </>
  );

  return (
    <h2 className={'text-base font-medium'}>
      {collapsible ? (
        <CollapsibleTrigger
          className={
            'group/child-header outline-focus-ring hover:bg-accent/40 -mx-2 flex w-[calc(100%+var(--spacing)*4)] items-center gap-2 rounded-md px-2 py-1 outline-0 transition-colors focus-visible:outline-3'
          }
        >
          {content}
        </CollapsibleTrigger>
      ) : (
        <span className={'flex items-center gap-2'}>{content}</span>
      )}
    </h2>
  );
}
